/**
 * Video carousel behaviour.
 *
 * Four things the shared `deferred-media` / `slideshow-component` pair does
 * not cover on its own:
 *
 * 1. Sound. `video.liquid` always renders the <video> muted so that autoplay
 *    contexts keep working. Playback here is click-initiated, so we hold a
 *    user gesture and are allowed to unmute once it starts.
 * 2. The control bar. `video.liquid` ships the volume + play/pause layout with
 *    a `hidden` class on its wrapper, but `updatePlayPauseHint` only clears
 *    `hidden` from the play/pause button itself — never the wrapper — so the
 *    bar stays display:none. Clear it when the video actually starts.
 * 3. Click-to-pause anywhere on the video, alongside the existing buttons.
 * 4. Carry-over autoplay. SlideshowComponent plays the incoming slide's media
 *    on every slide change; we gate that on whether the visitor was actually
 *    watching, so arrowing through the carousel stays quiet otherwise.
 */

const SECTION_SELECTOR = '.video-carousel';
const VOLUME_BUTTON_SELECTOR = '[ref="toggleVolumeButton"]';

/** Per-section state: is the visitor actively watching right now? */
const carouselState = new WeakMap();

/** @param {Element} section */
function stateFor(section) {
  let state = carouselState.get(section);
  if (!state) {
    state = { watching: false };
    carouselState.set(section, state);
  }
  return state;
}

/** @param {Element} media */
function revealControls(media) {
  media.querySelector('.video-interaction__controls')?.classList.remove('hidden');
}

/** @param {HTMLVideoElement} video */
function enableSound(video) {
  // Only force sound on the first play. If someone mutes it and replays, that
  // choice sticks.
  if (video.dataset.carouselSoundApplied) return;
  video.dataset.carouselSoundApplied = 'true';

  video.muted = false;
  if (video.volume === 0) video.volume = 1;

  // Repaint the speaker icon: updateVolumeButton ran at load time, when the
  // video was still muted.
  video.closest('deferred-media')?.updateVolumeButton?.(video);
}

/**
 * Gate the slide-change autoplay baked into SlideshowComponent. Returning
 * early here also means the video is never fetched, not merely started and
 * stopped.
 * @param {Element} media
 */
function gateAutoplay(media) {
  if (media.dataset.carouselGated || typeof media.playMedia !== 'function') return;
  media.dataset.carouselGated = 'true';

  const section = media.closest(SECTION_SELECTOR);
  if (!section) return;

  const play = media.playMedia.bind(media);
  media.playMedia = (...args) => (stateFor(section).watching ? play(...args) : undefined);
}

/** @param {ParentNode} root */
function gateAll(root) {
  root.querySelectorAll('deferred-media').forEach(gateAutoplay);
}

/** @param {Element} section */
function watchSection(section) {
  gateAll(section);
  // Swiper clones slides for loop mode, and the editor re-renders sections, so
  // keep gating whatever shows up later.
  new MutationObserver(() => gateAll(section)).observe(section, {
    childList: true,
    subtree: true,
  });
}

document.querySelectorAll(SECTION_SELECTOR).forEach(watchSection);

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target?.querySelector?.(SECTION_SELECTOR);
  if (section) watchSection(section);
});

// Capture phase: record what this click is about to do, before the theme's own
// handlers act on it. Every user-initiated play passes through here, which is
// what keeps the gate above from swallowing a deliberate click.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const media = target.closest(`${SECTION_SELECTOR} deferred-media`);
    if (!media) return;
    // Muting is not the same as stopping.
    if (target.closest(VOLUME_BUTTON_SELECTOR)) return;

    const section = media.closest(SECTION_SELECTOR);
    if (section) stateFor(section).watching = !media.hasAttribute('data-playing');
  },
  true
);

// Click anywhere on the video to toggle playback. The control buttons carry
// their own handlers, so leave those clicks to them.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const media = target.closest(`${SECTION_SELECTOR} deferred-media`);
  if (!media || target.closest('button')) return;
  // Before the first play the poster button owns the click.
  if (!media.hasAttribute('data-media-loaded')) return;

  media.toggleMedia?.();
});

// `play` does not bubble, so listen in the capture phase.
document.addEventListener(
  'play',
  (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;

    const section = video.closest(SECTION_SELECTOR);
    if (!section) return;

    const media = video.closest('deferred-media');
    if (media) revealControls(media);
    // Covers the carry-over case: a video started by a slide change keeps the
    // visitor marked as watching for the slide after it.
    stateFor(section).watching = true;

    // Autoplay has no user gesture behind it — unmuting there gets the whole
    // video blocked by the browser, so leave those alone.
    if (media?.hasAttribute('data-autoplay')) return;

    enableSound(video);
  },
  true
);
